import { tokenStore } from "@/lib/tokenStore";
import { apiFetch, refreshUserToken } from "@/lib/apiClient";
import { API_URL } from "@/lib/constants";
import type { User, LoginCredentials, RegisterData } from "../types";

export const authService = {
  async login(credentials: LoginCredentials) {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
      credentials: "include", // receives refresh_token cookie
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Login failed");
    }

    const data = await response.json();

    // Store access_token in memory (NOT in cookie or localStorage)
    if (data.access_token) {
      tokenStore.setUserToken(data.access_token);
    }

    return data;
  },

  async register(userData: RegisterData) {
    const { confirmPassword, ...backendData } = userData as any;
    void confirmPassword;
    const response = await fetch(`${API_URL}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(backendData),
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Registration failed");
    }

    return response.json();
  },

  async verifyOtp(email: string, otp: string) {
    const response = await fetch(`${API_URL}/auth/verify-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, otp }),
      credentials: "include", // receives refresh_token cookie
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Verification failed");
    }

    const data = await response.json();

    // Store access_token in memory
    if (data.access_token) {
      tokenStore.setUserToken(data.access_token);
    }

    return data;
  },

  async logout() {
    try {
      // Use apiFetch so the Bearer header is sent (backend may need it for session lookup)
      await apiFetch(`${API_URL}/auth/logout`, {
        method: "POST",
      });
    } catch {
      // Server-side logout failed, continue with client-side cleanup
    }
    // Clear in-memory token
    this.removeToken();
  },

  async getProfile() {
    const response = await apiFetch(`${API_URL}/auth/me`, {
      method: "GET",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch profile");
    }

    return response.json(); // returns { user }
  },

  async updateProfile(profileData: Partial<User>) {
    const response = await apiFetch(`${API_URL}/auth/profile`, {
      method: "PATCH",
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update profile");
    }

    return response.json(); // returns { user, message }
  },

  /**
   * Attempt a silent refresh to obtain a new access_token.
   * Called on page load (AuthContext mount) to rehydrate the in-memory token.
   * Returns true if successful.
   */
  async refreshAccessToken(): Promise<boolean> {
    return refreshUserToken();
  },

  setToken(token: string) {
    tokenStore.setUserToken(token);
  },

  getToken() {
    return tokenStore.getUserToken();
  },

  removeToken() {
    tokenStore.setUserToken(null);
    if (typeof window !== "undefined") {
      // Clean up any legacy localStorage entries
      localStorage.removeItem("auth_user");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    }
  },

  setUser(user: unknown) {
    void user;
  },

  getUser() {
    // Rely on getProfile() instead of localStorage caching
    return null;
  },

  async forgotPassword(email: string) {
    const response = await fetch(`${API_URL}/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to request password reset");
    }

    return response.json();
  },

  async resetPassword(token: string, newPass: string) {
    const response = await fetch(`${API_URL}/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, newPass }),
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to reset password");
    }

    return response.json();
  },
};
